'use client';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const registerSchema = z.object({
  name: z.string().min(2, 'Nom trop court'),
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Mot de passe trop court'),
  confirm: z.string()
}).refine((data) => data.password === data.confirm, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirm"]
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const router = useRouter();
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema)
  });

  const onSubmit = (data: RegisterForm) => {
    console.log('Register attempt:', data);
    router.push('/dashboard');
  };

  return (
    <div className="glass-card p-8">
      <h1 className="text-2xl font-display font-bold mb-6 text-center">Créer un compte</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-1">Nom complet</label>
          <input 
            type="text" 
            {...register('name')}
            className="w-full bg-surface-900 border border-glass-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="Jean Dupont"
          />
          {errors.name && <p className="text-danger text-sm mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-1">Email</label>
          <input 
            type="email" 
            {...register('email')}
            className="w-full bg-surface-900 border border-glass-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="jean@dupont.fr"
          />
          {errors.email && <p className="text-danger text-sm mt-1">{errors.email.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-1">Mot de passe</label>
          <input 
            type="password" 
            {...register('password')}
            className="w-full bg-surface-900 border border-glass-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="••••••••"
          />
          {errors.password && <p className="text-danger text-sm mt-1">{errors.password.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-1">Confirmer le mot de passe</label>
          <input 
            type="password" 
            {...register('confirm')}
            className="w-full bg-surface-900 border border-glass-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="••••••••"
          />
          {errors.confirm && <p className="text-danger text-sm mt-1">{errors.confirm.message}</p>}
        </div>
        
        <button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-surface-950 font-bold py-3 rounded-xl transition-all shadow-[0_0_15px_rgba(245,158,11,0.2)] mt-2">
          S'inscrire
        </button>
      </form>
      
      <p className="mt-6 text-center text-surface-400 text-sm">
        Déjà un compte ?{' '}
        <Link href="/login" className="text-amber-500 hover:underline font-medium">
          Se connecter
        </Link>
      </p>
    </div>
  );
}
